// Modal explaining the verification process
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';

export default function VerificationExplainerModal({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      label: 'We Check Your OnlyFans Account',
      description: 'We securely verify that you have an active OnlyFans account and fetch your subscription list.',
      icon: <SearchIcon />,
    },
    {
      label: 'Your Subscriptions Are Loaded',
      description: 'We retrieve the list of creators you\'re subscribed to on OnlyFans so you can review them.',
      icon: <VerifiedUserIcon />,
    },
    {
      label: 'You Can Write Reviews',
      description: 'Once verified, you can write authentic reviews for the creators you actually subscribe to.',
      icon: <RateReviewIcon />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          m: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle sx={{ pb: 2, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VerifiedUserIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              fontWeight={700}
              sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
            >
              OnlyFans Verification Process
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
            >
              How we verify your account
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          To ensure only real subscribers can write reviews, we verify your OnlyFans account and load your subscriptions.
        </Typography>

        <Stepper orientation="vertical" sx={{ mt: 2 }}>
          {steps.map((step, index) => (
            <Step key={index} active={true} completed={false}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="info.main">
            ⏱️ How Long Does It Take?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Without 2FA: 2-3 minutes
            <br />
            • With 2FA: 3-5 minutes (you'll need to enter a code)
            <br />
            <br />
            You can explore the site while verification happens in the background.
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.main">
            🔒 Your Privacy & Security
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Your OnlyFans password is encrypted
            <br />
            • We never access your messages or content
            <br />
            • We only read your subscription list
            <br />
            • You can delete your data anytime
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 1 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{ 
            borderRadius: 2,
            py: 1.5,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
}

